import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Sidebar from './components/Sidebar';
import LoginPage from './pages/LoginPage';
import AdminDashboard from './pages/AdminDashboard';
import AdminManage from './pages/AdminManage';
import AdminCandidates from './pages/AdminCandidates';
import AdminImport from './pages/AdminImport';
import AdminAnomalies from './pages/AdminAnomalies';
import AdminAssign from './pages/AdminAssign';
import TeamLeaderDashboard from './pages/TeamLeaderDashboard';
import TLAssign from './pages/TLAssign';
import SurveyorDashboard from './pages/SurveyorDashboard';
import SelfScanVote from './pages/SelfScanVote';
import { Loader2 } from 'lucide-react';
import './App.css';

function ProtectedRoute({ children, allowedRoles }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
    </div>
  );
  if (!user) return <Navigate to="/login" replace />;
  if (allowedRoles && !allowedRoles.includes(user.role)) return <Navigate to="/login" replace />;
  return children;
}

function DashboardLayout({ children }) {
  return (
    <>
      <Sidebar />
      <main className="main-content bg-slate-50 min-h-screen p-6">{children}</main>
    </>
  );
}

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
    </div>
  );
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === 'admin') return <Navigate to="/admin" replace />;
  if (user.role === 'team_leader') return <Navigate to="/team-leader" replace />;
  return <Navigate to="/surveyor" replace />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/vote/:token" element={<SelfScanVote />} />
      <Route path="/admin" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <DashboardLayout><AdminDashboard /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/manage" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <DashboardLayout><AdminManage /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/candidates" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <DashboardLayout><AdminCandidates /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/import" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <DashboardLayout><AdminImport /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/anomalies" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <DashboardLayout><AdminAnomalies /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/assign" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <DashboardLayout><AdminAssign /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/team-leader" element={
        <ProtectedRoute allowedRoles={['team_leader']}>
          <DashboardLayout><TeamLeaderDashboard /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/team-leader/assign" element={
        <ProtectedRoute allowedRoles={['team_leader']}>
          <DashboardLayout><TLAssign /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/surveyor" element={
        <ProtectedRoute allowedRoles={['surveyor']}>
          <DashboardLayout><SurveyorDashboard /></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/" element={<RootRedirect />} />
      <Route path="*" element={<RootRedirect />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}
